import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { SignaturePad } from '@/components/ui/signature-pad'
import { Modal } from '@/components/ui/modal'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api-client'
import { toast } from 'react-hot-toast'
import { formatDate } from '@shared/utils'

interface AssignmentApprovalModalProps {
  isOpen: boolean
  onClose: () => void
  assignment: any
  onSuccess: () => void
}

export function AssignmentApprovalModal({ 
  isOpen, 
  onClose, 
  assignment,
  onSuccess 
}: AssignmentApprovalModalProps) {
  const [signature, setSignature] = useState<string | null>(null)
  const [approvalNotes, setApprovalNotes] = useState('')
  const [action, setAction] = useState<'approve' | 'reject' | null>(null)

  const queryClient = useQueryClient()

  const approveMutation = useMutation({
    mutationFn: async (data: { employeeSignature: string; approvalNotes?: string }) => {
      await apiClient.put(`/assignments/${assignment.id}/approve`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      queryClient.invalidateQueries({ queryKey: ['device', assignment.deviceId] })
      toast.success('Assignment approved successfully')
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to approve assignment')
    }
  })

  const rejectMutation = useMutation({
    mutationFn: async (data: { approvalNotes?: string }) => {
      await apiClient.put(`/assignments/${assignment.id}/reject`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      queryClient.invalidateQueries({ queryKey: ['device', assignment.deviceId] })
      toast.success('Assignment rejected successfully')
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reject assignment')
    }
  })

  const handleApprove = () => {
    if (!signature) {
      toast.error('Please provide your signature to approve the assignment')
      return
    }

    approveMutation.mutate({
      employeeSignature: signature,
      approvalNotes: approvalNotes.trim() || undefined
    })
  }

  const handleReject = () => {
    rejectMutation.mutate({
      approvalNotes: approvalNotes.trim() || undefined
    })
  }

  const handleAction = (actionType: 'approve' | 'reject') => {
    setAction(actionType)
    if (actionType === 'reject') {
      handleReject()
    }
  }

  const isLoading = approveMutation.isPending || rejectMutation.isPending

  // Don't render if assignment is null
  if (!assignment) {
    return null
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="lg"
      title="Device Assignment Approval"
    >
      <div className="space-y-6">
        {/* Assignment Details */}
        <div className="bg-muted/50 rounded-lg p-4">
          <h3 className="font-semibold mb-3">Assignment Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Device:</span>
              <p className="text-muted-foreground">{assignment.device?.name || 'Unknown Device'}</p>
            </div>
            <div>
              <span className="font-medium">Assigned On:</span>
              <p className="text-muted-foreground">{formatDate(assignment.assignedOn)}</p>
            </div>
            <div>
              <span className="font-medium">Status:</span>
              <Badge variant="outline" className="ml-2">
                {assignment.status}
              </Badge>
            </div>
            {assignment.notes && (
              <div className="md:col-span-2">
                <span className="font-medium">Admin Notes:</span>
                <p className="text-muted-foreground">{assignment.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Signature Section */}
        {action === 'approve' && (
          <div>
            <SignaturePad
              onSignatureChange={setSignature}
              width={400}
              height={150}
            />
          </div>
        )}

        {/* Notes Section */}
        <div className="space-y-2">
          <Label htmlFor="approvalNotes">
            {action === 'approve' ? 'Approval Notes (Optional)' : 'Rejection Reason (Optional)'}
          </Label>
          <Textarea
            id="approvalNotes"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder={
              action === 'approve' 
                ? 'Add any notes about the device assignment...'
                : 'Please provide reason for rejecting this assignment...'
            }
            rows={3}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          
          {!action ? (
            <>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleAction('reject')}
                disabled={isLoading}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject Assignment
              </Button>
              <Button
                type="button"
                onClick={() => setAction('approve')}
                disabled={isLoading}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Approve Assignment
              </Button>
            </>
          ) : action === 'approve' ? (
            <Button
              type="button"
              onClick={handleApprove}
              disabled={isLoading || !signature}
            >
              {isLoading ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4" />
              )}
              Confirm Approval
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}
